import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * Button — DESIGN_SYSTEM.md §2.1
 *
 * Variant naming pragmatically diverges from the spec to avoid a 300+ call-site
 * rename. Spec name → our name:
 *   spec "primary"     → primary
 *   spec "ghost"       → outline       (white + line border + ink text + hover:brand)
 *   spec "subtle"      → ghost         (transparent → surface-2 hover)
 *   spec "outline"     → outline-brand (brand border + brand text)
 *   spec "destructive" → destructive
 *   spec "link"        → link
 *
 * Legacy aliases (deprecated, kept for compat):
 *   default   → primary
 *   secondary → outline
 *   danger    → destructive
 *   subtle    → ghost
 */

const buttonVariants = cva(
  // Base
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-medium transition-colors',
    'disabled:opacity-50 disabled:pointer-events-none',
    'tap-highlight-transparent',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-brand-500 text-white hover:bg-brand-700 active:bg-brand-700',
        outline:
          'bg-surface text-ink border border-line hover:border-brand-300 hover:text-brand-500',
        'outline-brand':
          'bg-surface text-brand-500 border border-brand-500 hover:bg-brand-50',
        ghost:
          'bg-transparent text-ink hover:bg-surface-2',
        destructive:
          'bg-danger text-white hover:bg-danger-text',
        'outline-danger':
          'bg-surface text-danger border border-danger hover:bg-danger/10',
        success:
          'bg-success text-white hover:bg-success-text',
        link:
          'bg-transparent text-brand-500 underline-offset-4 hover:underline px-0',
        // Legacy aliases — migrate over time
        default:
          'bg-brand-500 text-white hover:bg-brand-700 active:bg-brand-700',
        secondary:
          'bg-surface text-ink border border-line hover:border-brand-300 hover:text-brand-500',
        danger:
          'bg-danger text-white hover:bg-danger-text',
        subtle:
          'bg-transparent text-ink hover:bg-surface-2',
      },
      size: {
        sm:    'h-[30px] px-3 text-xs rounded-md',
        md:    'h-9 px-3.5 text-[13px] rounded-md',
        lg:    'h-12 px-5 text-[14.5px] rounded-lg',
        block: 'h-12 w-full px-5 text-[14.5px] rounded-lg',
        icon:  'h-9 w-9 p-0 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size:    'md',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, loading, disabled, asChild, iconLeft, iconRight, children, ...props },
    ref,
  ) => {
    const composedClassName = cn(buttonVariants({ variant, size, className }), 'relative');

    // asChild path: Radix Slot requires exactly one element child. The
    // loading/iconLeft/iconRight scaffolding would create multiple children
    // and trip React.Children.only — so when asChild we pass `children`
    // through directly. Callers using `asChild` are expected to compose
    // their own icons inside the child element.
    if (asChild) {
      return (
        <Slot ref={ref} className={composedClassName} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={composedClassName}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <Loader2 className="absolute h-4 w-4 animate-spin" aria-hidden="true" />
        )}
        <span
          className={cn(
            'inline-flex items-center gap-2',
            loading && 'invisible',
          )}
        >
          {iconLeft}
          {children}
          {iconRight}
        </span>
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
